import { figureService } from './figureService.js';

// Regex patterns for matching @{figure:...} and @{numeral:...} in text
const FIGURE_PATTERN = /@\{figure:([^}]+)\}/g;
const NUMERAL_PATTERN = /@\{numeral:([^_}]+)_([^}]+)\}/g;

/**
 * Unified reference service for handling all @ linking and export functionality
 * Single source of truth for figures, numerals, and tables
 */
export class ReferenceService {
    /**
     * Get all available reference items for @ linking menu
     * @returns {Promise<Array>} Combined list of figures, numerals, and tables
     */
    async getMenuItems() {
        return await figureService.getFigureMenuItems();
    }

    /**
     * Insert a reference when user selects from @ menu or toolbar
     * @param {Object} editor - TipTap editor instance
     * @param {Object} item - Reference item {id, name, type, figureId?, value?}
     */
    async insertReference(editor, item) {
        if (!editor || !item) return;
        
        const { figureKey, displayText } = this._formatReference(item);
        
        // Insert reference with unified pattern
        editor.chain()
            .focus()
            .setFigureReference({ figureKey })
            .insertContent(displayText)
            .removeFigureReference()
            .run();
    }

    /**
     * Format reference for insertion - creates both the key and display text
     * @param {Object} item - Reference item
     * @returns {Object} {figureKey, displayText}
     */
    _formatReference(item) {
        let figureKey, displayText;
        
        switch(item.type) {
            case 'numeral':
                figureKey = `numeral:${item.figureId}:${item.id}`;
                displayText = `@{numeral:${item.name}_${item.value}}`;
                break;
                
            case 'table':
                figureKey = `table:${item.id}`;
                displayText = `@{table:${item.name}}`;
                break;
                
            case 'figure':
            default:
                figureKey = item.id;
                displayText = `@{figure:${item.name}}`;
                break;
        }
        
        return { figureKey, displayText };
    }

    /**
     * Create reference map for export - maps figure keys to their export format
     * @param {Document} doc - Parsed HTML document
     * @returns {Promise<Map>} Map of figure keys to export text (FIG. X, {name} {value}, TABLE X)
     */
    async createExportMap(doc) {
        const exportMap = new Map();
        const figureOrder = [];
        const tableOrder = [];
        
        // Get numeral data for cross-referencing - handle failures gracefully
        let numerals = [];
        let figures = [];
        try {
            numerals = await figureService.fetchNumerals();
            figures = await figureService.fetchFigures();
        } catch (error) {
            console.warn('Failed to fetch numerals for export, will use fallback formatting:', error.message);
        }
        
        // Create a map of figureId to figure index for calculating X0Y format
        const figureIdToIndex = new Map();
        figures.forEach((figure, index) => {
            figureIdToIndex.set(figure.id, index + 1); // 1-based indexing
        });
        
        const numeralsByKey = new Map();
        numerals.forEach(n => {
            // Calculate X0Y format for export
            let displayValue = n.value;
            if (n.figureId && figureIdToIndex.has(n.figureId) && !n.isCrossReference) {
                const figureNumber = figureIdToIndex.get(n.figureId);
                const paddedNumeralValue = String(n.value).padStart(2, '0');
                displayValue = parseInt(`${figureNumber}${paddedNumeralValue}`);
            }
            
            numeralsByKey.set(`numeral:${n.figureId}:${n.id}`, {
                ...n,
                value: displayValue // Use calculated X0Y format for export
            });
        });

        // Find all references in document order
        const walker = document.createTreeWalker(
            doc.body,
            NodeFilter.SHOW_ELEMENT,
            { acceptNode: node => node.hasAttribute('data-figure-key') ? 
                NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP }
        );

        let node;
        while ((node = walker.nextNode())) {
            const figureKey = node.getAttribute('data-figure-key');
            if (!figureKey) continue;
            
            if (figureKey.startsWith('numeral:')) {
                // Handle numeral references
                const numeral = numeralsByKey.get(figureKey);
                if (numeral) {
                    const figureId = numeral.figureId;
                    if (!figureOrder.includes(figureId)) {
                        figureOrder.push(figureId);
                    }
                    exportMap.set(figureKey, `${numeral.name} ${numeral.value}`);
                }
            } else if (figureKey.startsWith('table:')) {
                // Handle table references
                if (!tableOrder.includes(figureKey)) {
                    tableOrder.push(figureKey);
                }
                exportMap.set(figureKey, `TABLE ${tableOrder.length}`);
            } else {
                // Handle figure references
                if (!figureOrder.includes(figureKey)) {
                    figureOrder.push(figureKey);
                }
                exportMap.set(figureKey, `FIG. ${figureOrder.indexOf(figureKey) + 1}`);
            }
        }

        return exportMap;
    }

    /**
     * Process text content and replace references with export format
     * @param {Element} element - DOM element to process
     * @param {Map} exportMap - Map from createExportMap()
     * @returns {string} Processed text with references replaced
     */
    processReferences(element, exportMap) {
        const clone = element.cloneNode(true);
        
        // Replace all data-figure-key elements
        const refs = clone.querySelectorAll('[data-figure-key]');
        refs.forEach(refElement => {
            const figureKey = refElement.getAttribute('data-figure-key');
            const exportText = exportMap.get(figureKey) || '';
            
            // Remove @ from previous text node if present
            const prevSibling = refElement.previousSibling;
            if (prevSibling?.nodeType === Node.TEXT_NODE && prevSibling.textContent.endsWith('@')) {
                prevSibling.textContent = prevSibling.textContent.slice(0, -1);
            }
            
            // Replace with export text
            refElement.parentNode.replaceChild(document.createTextNode(exportText), refElement);
        });
        
        return clone.textContent.trimEnd();
    }
}

export const referenceService = new ReferenceService();